import { type CellTemplateProp, type ColumnRegular, type HyperFunc, RevoGrid, Template, type VNode, defineCustomElements } from '@revolist/react-datagrid';
import SelectTypePlugin from '@revolist/revogrid-column-select';
import { useEffect, useRef } from 'react';

// Revogrid cells
import { SupplierActionsCell } from "@/components/revogrid-cells/SupplierActionsCell";
import { computeRevoGridHeight } from '@/helpers/revogrid';
import { useDataTable } from '@/hooks/use-data-table';
import { useRevoGridEdit } from '@/hooks/use-revogrid-edit';
import { suppliersPath } from '@/routes';
import { supplierPath } from '@/routes';
import type { PagyMetadata, RansackFilters, Supplier } from '@/types';

defineCustomElements();

interface Category {
  id: number;
  name: string;
}

interface Subcategory {
  id: number;
  name: string;
  category_id: number;
}

interface SuppliersRevoGridProps {
  suppliers: Supplier[];
  pagy: PagyMetadata;
  filters: RansackFilters;
  categories: Category[];
  subcategories: Subcategory[];
}

export function SuppliersRevoGrid({
  suppliers,
  pagy,
  filters,
}: SuppliersRevoGridProps) {
  // Revogrid ref
  const gridRef = useRef<HTMLRevoGridElement>(null);

  // Flag to prevent filter apply on initial mount
  const isInitialMount = useRef(true);

  // Use shared data table logic for filter management
  const { handleFilterChange } = useDataTable({ filters, pagy, path: suppliersPath() });

  // categoryOptions and subcategoryOptions no longer needed - columns are read-only

  // Transform data for select: null/undefined → 0 for correct visualization
  const transformedSuppliers = suppliers.map(supplier => ({
    ...supplier,
    category_id: supplier.category?.id ?? 0,
    subcategory_id: supplier.subcategory?.id ?? 0,
  }));

  // Calculate grid height to avoid internal scroll
  const gridHeight = computeRevoGridHeight(transformedSuppliers.length);

  // Column definitions following RevoGrid patterns
  const columns: ColumnRegular[] = [
    // ID - read only
    {
      prop: 'id',
      name: 'ID',
      readonly: true,
      size: 80,
      minSize: 70,
      filter: 'number',
    },
    // Code - editable text with filter
    {
      prop: 'code',
      name: 'Código',
      size: 120,
      minSize: 100,
      filter: 'string',
    },
    // Name - editable text with filter
    {
      prop: 'name',
      name: 'Nombre',
      size: 200,
      minSize: 150,
      filter: 'string',
      pin: 'colPinStart'
    },
    // Supplier type - editable text with filter
    {
      prop: 'supplier_type',
      name: 'Tipo de proveedor',
      size: 180,
      minSize: 150,
      filter: 'string',
      readonly: true,
    },
    // Email - editable text with filter and custom display
    {
      prop: 'email',
      name: 'Email',
      size: 200,
      minSize: 150,
      filter: 'string',
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const email = String(cellProps.value ?? '');
        if (email) {
          return createElement('a', {
            href: `mailto:${email}`,
            className: 'text-blue-600 hover:underline text-sm'
          }, email);
        }
        return createElement('span', {
          className: 'text-muted-foreground text-sm'
        }, '-');
      }
    },
    // Phone - editable text
    {
      prop: 'phone',
      name: 'Teléfono',
      size: 130,
      minSize: 120,
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const phone = String(cellProps.value ?? '');
        const displayText = phone || '-';
        return createElement('div', {
          className: `flex items-center h-full px-2 text-sm ${phone ? '' : 'text-muted-foreground'}`
        }, displayText);
      }
    },
    // Category - read-only display
    {
      prop: 'category_id',
      name: 'Categoría',
      readonly: true,
      size: 150,
      minSize: 120,
      // Custom display template to show category name
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const model = cellProps.model as Supplier;
        const displayText = model.category?.name ?? '-';
        return createElement('div', {
          className: `flex items-center h-full px-2 text-sm ${model.category?.name ? '' : 'text-muted-foreground'}`
        }, displayText);
      }
    },
    // Subcategory - read-only display
    {
      prop: 'subcategory_id',
      name: 'Subcategoría',
      readonly: true,
      size: 150,
      minSize: 120,
      // Custom display template to show subcategory name
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const model = cellProps.model as Supplier;
        const displayText = model.subcategory?.name ?? '-';
        return createElement('div', {
          className: `flex items-center h-full px-2 text-sm ${model.subcategory?.name ? '' : 'text-muted-foreground'}`
        }, displayText);
      }
    },
    // Actions column
    {
      prop: 'actions',
      name: 'Acciones',
      readonly: true,
      cellTemplate: Template(SupplierActionsCell),
      size: 100,
      pin: 'colPinEnd',
      minSize: 100,
      maxSize: 100,
      filter: false,
    },
  ];

  // Register column plugins
  const columnTypes = {
    select: new SelectTypePlugin()
  };

  /**
   * Handle RevoGrid filter events and convert to Ransack format
   * Maps RevoGrid filter events to Ransack query parameters
   */
  useEffect(() => {
    const handleFilterApply = (e: Event) => {
      // Skip filter apply on initial mount to prevent page reset
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }

      const detail = (e as CustomEvent<{ collection: Record<string, { value: string | number; type: string }> }>).detail;
      const { collection } = detail;

      const ransackFilters: Partial<RansackFilters> = {};

      // Map RevoGrid filters to Ransack format
      Object.entries(collection).forEach(([prop, filterData]) => {
        const value = filterData.value;
        const type = filterData.type;

        if (value === undefined || value === null || value === '') return;

        // Handle different filter types and convert to Ransack
        switch (type) {
          case 'contains':
            if (prop === 'name') ransackFilters.name_cont = String(value);
            if (prop === 'email') ransackFilters.email_cont = String(value);
            if (prop === 'code') ransackFilters.code_cont = String(value);
            break;
          case 'eq':
            if (prop === 'id') ransackFilters.id_eq = String(value);
            break;
          case 'eqN':
            if (prop === 'id') ransackFilters.id_eq = String(value);
            break;
        }
      });

      // Apply filters through the data table hook
      handleFilterChange(ransackFilters);
    };

    const gridEl = gridRef.current;
    if (gridEl) {
      gridEl.addEventListener('afterFilterApply', handleFilterApply);
      return () => gridEl.removeEventListener('afterFilterApply', handleFilterApply);
    }
  }, [handleFilterChange]);

  // Handle RevoGrid cell editing with reusable hook
  useRevoGridEdit<Supplier>({
    gridRef,
    pathGenerator: (supplier) => supplierPath(supplier),
    resourceKey: 'supplier',
    valueProcessor: (prop, newValue) => {
      // Handle select fields: 0 = null (unassigned), number = ID
      if (prop === 'category_id' || prop === 'subcategory_id') {
        return (newValue === 0 || newValue === '0' || newValue === undefined)
          ? null
          : Number(newValue);
      }
      return newValue as string | number | null;
    }
  });

  return (
    <div className="min-h-[400px]">
      <RevoGrid
        ref={gridRef}
        source={transformedSuppliers}
        columns={columns}
        columnTypes={columnTypes}
        theme="material"
        hideAttribution
        stretch={true}
        resize={true}
        autoSizeColumn={true}
        filter={true}
        // Force height to fit all rows (no internal scroll)
        style={{ height: `${gridHeight}px` }}
      />
    </div>
  );
}
